interface ICustomWindow extends Window {
  TAG?: string
}

declare var window: ICustomWindow

const TAG_URI = '/tag.js'

const REQUEST_TIMEOUT = 10000

const TAG_PATTERN = /window\.TAG\s*=\s*["']([^"']*)["']/

export const getRunningTag = () => window.TAG || ''

export const LOCAL_VERSION = 'local'

export const getVersionLabel = () => {
  const running = getRunningTag()

  return running.slice(running.indexOf(':') + 1).trim() || LOCAL_VERSION
}

export const fetchDeployedTag = async (): Promise<string> => {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

  try {
    const response = await fetch(`${TAG_URI}?${Date.now()}`, {
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
      },
      signal: controller.signal,
    })

    if (!response.ok) {
      throw new Error(`Tag request failed with status ${response.status}`)
    }

    return TAG_PATTERN.exec(await response.text())?.[1] ?? ''
  } finally {
    clearTimeout(timeout)
  }
}

const WARM_URIS = [TAG_URI, '/env-config.js']

const ATTEMPT_KEY = 'newVersionReloadAttempt'

export const markReloadAttempt = (tag: string) => {
  try {
    sessionStorage.setItem(ATTEMPT_KEY, tag)
  } catch {}
}

export const wasReloadAttempted = (tag: string) => {
  try {
    return !!tag && sessionStorage.getItem(ATTEMPT_KEY) === tag
  } catch {
    return false
  }
}

export const getHardReloadShortcut = () =>
  /Mac|iPhone|iPad/.test(navigator.userAgent)
    ? 'Cmd + Shift + R'
    : 'Ctrl + Shift + R'

export const reloadApp = async (tag: string) => {
  markReloadAttempt(tag)

  try {
    await Promise.all(WARM_URIS.map(uri => fetch(uri, { cache: 'reload' })))
  } catch {}

  window.location.reload()
}

export const findNewVersion = async (): Promise<string> => {
  const running = getRunningTag()
  if (!running) {
    return ''
  }

  const deployed = await fetchDeployedTag()

  return deployed && deployed !== running ? deployed : ''
}

const DISMISSED_KEY = 'newVersionDismissed'

const SNOOZE = 4 * 60 * 60 * 1000

export const SNOOZE_HOURS = SNOOZE / (60 * 60 * 1000)

interface IDismissal {
  tag: string
  until: number | null
}

const readDismissal = (): IDismissal | null => {
  try {
    const stored = sessionStorage.getItem(DISMISSED_KEY)
    if (!stored) {
      return null
    }
    const dismissal = JSON.parse(stored)
    return typeof dismissal?.tag === 'string' ? dismissal : null
  } catch {
    return null
  }
}

export const isVersionHidden = (tag: string, now = Date.now()) => {
  const dismissal = readDismissal()

  if (!dismissal || dismissal.tag !== tag) {
    return false
  }

  return dismissal.until === null || now < dismissal.until
}

export const hideVersion = (tag: string, forGood: boolean) => {
  try {
    sessionStorage.setItem(
      DISMISSED_KEY,
      JSON.stringify({ tag, until: forGood ? null : Date.now() + SNOOZE }),
    )
  } catch {
    // nothing to do, the bar simply stays until the next check
  }
}
